'use client';

import type { MessageKeys } from 'next-intl';
import type { useUserCriteria } from '@/app/hooks/useUserCriteria';
import type { Messages } from '@/global';
import type { CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import type { UserType } from '~/model/UserType';
import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import { VtmnSkeleton } from '@vtmn/react';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { Criteria } from '@/app/[locale]/profile/components/Criteria/Criteria';
import { useCriterion } from '@/app/hooks/useCriterion';
import { BeardmanTalking } from '~/components/Beardman/BeardmanTalking/BeardmanTalking';
import { buttonValidation } from '~/components/commons/CompleteProfileBanner/utils';
import { MainColumn } from '~/components/commons/MainColumn/MainColumn';
import { retrieveTranslatedLabel } from '~/components/Interaction/Form/utils';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { InfoBar } from '~/components/Journey/InfoBar/InfoBar';
import { AcceptLanguage } from '~/const/locale';
import { isMainUser } from '~/utils/user/user';

export const CriterionForm = ({
  criterion,
  description,
  lang,
  user,
  userCriteria,
  isLast,
  isProcessing,
  submit,
}: {
  criterion: CriteriaType;
  description: MessageKeys<
    Messages,
    'HOMEPAGE.COMPLETE_PROFILE.CRITERIA' | 'FORM.MISSING_CRITERIA_HEADER_NOTE'
  >;
  lang?: string;
  user: UserType;
  userCriteria: CriteriaChoiceUserType[];
  isLast: boolean;
  isProcessing: boolean;
  submit: ReturnType<typeof useUserCriteria>['submit'];
}) => {
  const t = useTranslations();
  const locale = useLocale();

  const { updatingData, onChange, resetUpdatingData } = useCriterion({
    criterion,
    userCriteria,
  });

  const disabled = useMemo(() => {
    const hasNoUpdatedAnswer
      = !userCriteria?.some(
        userCriterion => userCriterion.criterion['@id'] === criterion?.['@id'],
      ) && !updatingData[criterion?.['@id']]?.value;
    return isProcessing || !criterion || hasNoUpdatedAnswer;
  }, [criterion, isProcessing, updatingData, userCriteria]);

  const buttonValidationInfo = buttonValidation(isLast);

  const handleSubmit = async () => {
    const data = updatingData;
    resetUpdatingData();
    await submit(data);
  };

  const activeUserCriterion = useMemo(() => userCriteria?.find(
    userCriterion => userCriterion.criterion['@id'] === criterion?.['@id'],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [userCriteria, criterion?.['@id']]);

  useEffect(() => {
    if (activeUserCriterion?.['@id']) {
      onChange({
        criteriaUri: activeUserCriterion.criterion['@id'],
        type: activeUserCriterion.criterion.type,
        value: {
          choices: activeUserCriterion.choices.map(choice => choice['@id']),
          value_int: activeUserCriterion.value_int,
          value_date: activeUserCriterion.value_date,
        },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserCriterion?.['@id']]);

  return (
    <>
      <BeardmanTalking
        title={
          lang
            ? (
                <label lang={lang} htmlFor={`criteria-${criterion?.id}`}>
                  {retrieveTranslatedLabel(criterion, AcceptLanguage[locale])}
                </label>
              )
            : retrieveTranslatedLabel(criterion, AcceptLanguage[locale])
        }
        description={t(description)}
      />
      <div className="container-fluid-centered">
        <MainColumn column={journeyColumnTemplate}>
          {!criterion
            ? (
                <VtmnSkeleton height="48px" />
              )
            : (
                <Criteria
                  key={criterion?.['@id']}
                  criteria={criterion}
                  userCriteria={userCriteria?.find(
                    userCriterion =>
                      userCriterion.criterion['@id'] === criterion?.['@id'],
                  )}
                  updatingUserValue={updatingData[criterion?.['@id']]?.value}
                  handleChange={onChange}
                  noLabel
                />
              )}
          <div className="form-actions">
            <VpButton
              id="submit-criterion"
              data-cy="submitButton"
              disabled={disabled}
              {...(buttonValidationInfo.icon && {
                endSlot: <VpIcon name={buttonValidationInfo.icon} />,
              })}
              type="submit"
              loading={isProcessing}
              onClick={handleSubmit}
            >
              {t(buttonValidationInfo.title)}
            </VpButton>
          </div>
          {isMainUser(user) && (
            <InfoBar content={t('PROFILE.COMPLETION.COMMON.DATA_INFO')} />
          )}
        </MainColumn>
      </div>
    </>
  );
};
